import { Inject, Injectable } from '@angular/core';
import { EntityService } from '@shared/services/entity.service';
import { EntityFeatureNameEnum } from '@shared/contracts/entity-store.contract';
import {
  CompanyBasicInfoInput,
  CompanyType,
  JobType,
} from '@generated/graphql';
import { Company } from '@modules/company/models/company.model';
import {
  COMPANY_STORE,
  CompanyStore,
} from '@modules/company/store/company.store';
import { Store } from '@ngrx/store';
import {
  companyFactory,
  CompanyFactory,
} from '@modules/company/factories/company.factory';
import { map, Observable } from 'rxjs';
import { CompanyApi } from '@modules/company/api/company.api';

type CompanyEntityRequestOptions = {
  swallowError?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class CompanyService extends EntityService<
EntityFeatureNameEnum.Company,
CompanyType,
Company
> {
  private readonly kycEntityRefreshSeed$ = this.companyApi.findKycPatch$.bind(this.companyApi);

  constructor(
    protected readonly store: Store,
    @Inject(COMPANY_STORE) protected readonly entityStore: CompanyStore,
    protected readonly companyApi: CompanyApi,
  ) {
    super(store, entityStore);
  }

  missingCompanyStakeholderKycDocuments$ = this.activeEntity$.pipe(
    map(company => company?.missingCompanyStakeholderKycDocuments ?? [])
  )

  itemsToVerify$ = this.activeEntity$.pipe(
    map(company => company?.itemsToVerify?.items ?? [])
  )

  get factory(): CompanyFactory {
    return companyFactory;
  }

  private readonly infoEntityRefreshPlan = {
    mode: 'patch' as const,
    patch$: this.companyApi.findInfoViewPatch$.bind(this.companyApi),
  };

  private createPatchEntityRefreshPlan(
    patch$: (id: Company['id']) => Observable<Partial<CompanyType>>,
    options: {
      seed$?: (id: Company['id']) => Observable<CompanyType | null>;
    } = {},
  ) {
    return {
      mode: 'patch' as const,
      seed$: options.seed$,
      patch$,
    };
  }

  private toTrackingOptions(
    options: CompanyEntityRequestOptions,
    defaultSwallowError?: boolean,
  ) {
    return {
      swallowError: options.swallowError ?? defaultSwallowError,
    };
  }

  refreshActiveEntityPatch$(
    id: Company['id'],
    companyPatch$: Observable<Partial<CompanyType>>,
    options: CompanyEntityRequestOptions = {},
  ): Observable<Company> {
    return this.refreshEntityByPlan$(
      id,
      this.createPatchEntityRefreshPlan(() => companyPatch$),
      this.toTrackingOptions(options, true),
    );
  }

  refreshActiveInfoEntity$(
    id: Company['id'],
    options: CompanyEntityRequestOptions = {},
  ): Observable<Company> {
    return this.refreshEntityByPlan$(
      id,
      this.infoEntityRefreshPlan,
      this.toTrackingOptions(options, true),
    );
  }

  refreshActiveKycEntity$(
    id: Company['id'],
    options: CompanyEntityRequestOptions = {},
  ): Observable<Company> {
    return this.refreshEntityByPlan$(
      id,
      this.createPatchEntityRefreshPlan(this.companyApi.findKycPatch$.bind(this.companyApi)),
      this.toTrackingOptions(options, true),
    );
  }

  refreshActiveSubscriptionsEntity$(
    id: Company['id'],
    options: CompanyEntityRequestOptions = {},
  ): Observable<Company> {
    return this.refreshEntityByPlan$(
      id,
      this.createPatchEntityRefreshPlan(this.companyApi.findSubscriptionsPatch$.bind(this.companyApi)),
      this.toTrackingOptions(options, true),
    );
  }

  submitKyc$(
    id: Company['id'],
    options: CompanyEntityRequestOptions = {},
  ): Observable<Company> {
    return this.refreshEntityByPlan$(
      id,
      this.createPatchEntityRefreshPlan(
        this.companyApi.submitKycPatch$.bind(this.companyApi),
        {
          seed$: this.kycEntityRefreshSeed$,
        },
      ),
      this.toTrackingOptions(options),
    );
  }

  confirmKyc$(
    id: Company['id'],
    options: CompanyEntityRequestOptions = {},
  ): Observable<Company> {
    return this.refreshEntityByPlan$(
      id,
      this.createPatchEntityRefreshPlan(
        this.companyApi.confirmKycPatch$.bind(this.companyApi),
        {
          seed$: this.kycEntityRefreshSeed$,
        },
      ),
      this.toTrackingOptions(options),
    );
  }

  public expertChangeCompanyStatus(
    id: string,
    input: CompanyBasicInfoInput,
  ): Observable<boolean> {
    return this.companyApi.expertChangeCompanyStatus$(id, input);
  }

  public getChangeCompanyStatusJobRequest(id: string): Observable<JobType> {
    return this.companyApi.getChangeCompanyStatusJobRequest$(id);
  }

  public confirmChangeCompanyStatusJob(
    jobId: string,
    effectiveDate: string,
  ): Observable<boolean> {
    return this.companyApi.confirmChangeCompanyStatusJob$(jobId, effectiveDate);
  }
}
